import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Image,
  Input,
  CloseButton,
  Tooltip,
  Textarea,
  FormLabel,
  FormControl,
  Button,
  InputGroup,
  InputRightElement,
  Text,
  Portal,
  useBreakpointValue
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import MathJax from '../MathJax';
import './AIChat.scss';
import CustomModal from '../Modal';

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

const CollapseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
  </svg>
);

export const AIChat = ({
  chatLoading,
  chat,
  toggleAIChat,
  toggleChat,
  followUpQuestions = [],
  askFollowUPQuestion,
  onReview,
  openModal,
  closeModal,
  regenerate,
  feedback,
  setFeedback,
  submitFeedback,
  feedbackResponse,
  askFirstQuestion,
  hasReachedMaxLimit,
  // Learn mode: Ninja is shown but can't be asked until the question is answered.
  isLocked = false,
  // Distance from the top of the window to where the docked desktop panel sticks (below the page's sticky headers).
  dockOffset = 100
}) => {
  // Desktop: Ninja sits in a side column next to the question. Smaller screens keep the floating button + overlay.
  const isDocked = useBreakpointValue({ base: false, lg: true })

  const chatBodyRef = useRef()

  const [followUpQues, setFollowUpQues] = useState("")
  // Full screen chat (both desktop and mobile), for long explanations.
  const [isExpanded, setIsExpanded] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(true)

  const initialRef = React.useRef(null)

  // Bring the newest exchange into view (its top, so a long answer is read from the start).
  useEffect(() => {
    const body = chatBodyRef.current
    if (!body) return
    const turns = body.querySelectorAll(".chat-turn")
    const lastTurn = turns[turns.length - 1]
    if (lastTurn) body.scrollTop = lastTurn.offsetTop - 8
  }, [chat.length, chatLoading, toggleChat, isExpanded])

  // A locked (not yet answered) question never shows a chat, even one left open on the previous question.
  const showChat = !toggleChat && !isLocked
  const isFullScreen = showChat && isExpanded

  // Esc leaves full screen.
  useEffect(() => {
    if (!isFullScreen) return
    const onKeyDown = (e) => { if (e.key === "Escape") setIsExpanded(false) }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isFullScreen])

  const closeChat = () => {
    setIsExpanded(false)
    toggleAIChat()
  }

  const openChat = () => (chat.length > 0 ? toggleAIChat() : askFirstQuestion())

  const sendFollowUp = () => {
    const message = followUpQues.trim()
    if (!message || chatLoading) return
    askFollowUPQuestion(message)
    setFollowUpQues("")
  }

  const chatPanel = (
    // Header, scrolling messages and the follow-up area are stacked with flexbox, so they never overlap.
    <Box className='chat-container'>
      <Box className="chat-header">
        <Flex alignItems="center" minWidth={0}>
          <Image className="profile-pic" src="/images/ninja.png" alt="" />
          <Box className="chat-info">
            <Box className="chat-with">Ask AI Ninja</Box>
            <Box className="chat-disclaimer">Can make mistakes. Please verify.</Box>
          </Box>
        </Flex>
        <Flex alignItems="center" gap="1" flex="none">
          <Tooltip label={isExpanded ? "Exit full screen" : "Full screen"}>
            <button
              type="button"
              className="chat-header__btn"
              aria-label={isExpanded ? "Exit full screen" : "Full screen"}
              aria-pressed={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
            </button>
          </Tooltip>
          <CloseButton size='md' aria-label="Close chat" onClick={closeChat} />
        </Flex>
      </Box>
      <Box className="chat-body" ref={chatBodyRef}>
        {
          chat.length > 0 ?
            React.Children.toArray(chat.map((c, index) =>
              <Box className="chat-turn">
                <Flex justifyContent="flex-end">
                  <MathJax mathData={c.question} id={`${index}+10`} className="message sent" />
                </Flex>
                {chatLoading && c.response === "" ?
                  <Box className="chat-typing" aria-label="Ninja is typing">
                    <span /><span /><span />
                  </Box> :
                  <MathJax mathData={c.response} id={index} className="message received" />
                }
                {
                  c.response && (chat.length === index + 1) ?
                    <Flex className="chat-feedback">
                      <Tooltip label='Helpful'>
                        <button type="button" aria-label="Helpful" onClick={() => onReview(!c.like, false, index)}>
                          <Image src={c.like ? "/images/thumbs-up-dark.png" : "/images/thumbs-up.png"} alt="" />
                        </button>
                      </Tooltip>
                      <Tooltip label='Not helpful'>
                        <button type="button" aria-label="Not helpful" onClick={() => c.dislike ? onReview(false, false, index) : onReview(false, true, index)}>
                          <Image src={c.dislike ? "/images/thumbs-down-dark.png" : "/images/thumbs-down.png"} alt="" />
                        </button>
                      </Tooltip>
                      <Tooltip label='Regenerate the response'>
                        <button type="button" aria-label="Regenerate the response" onClick={() => regenerate(c.question)}>
                          <Image src="/images/regenerate.png" alt="" />
                        </button>
                      </Tooltip>
                    </Flex> :
                    c.response && (c.like || c.dislike) ?
                      <Flex className="chat-feedback chat-feedback--static">
                        <Image src={c.like ? "/images/thumbs-up-dark.png" : "/images/thumbs-down-dark.png"} alt={c.like ? "Marked helpful" : "Marked not helpful"} />
                      </Flex> : null
                }
              </Box>
            )) :
            <Flex className="chat-start">
              <Button className="ninja-card__action" onClick={askFirstQuestion}>
                <span aria-hidden="true">✨</span> Explain it step by step
              </Button>
            </Flex>
        }
      </Box>
      {
        hasReachedMaxLimit ?
          <Box className="chat-footer chat-footer--note">You've reached the follow-up limit for this question.</Box> :
          followUpQuestions.length > 0 ?
            <Box className="chat-footer chat-footer--suggestions">
              <button
                type="button"
                className="chat-footer__label"
                aria-expanded={showSuggestions}
                onClick={() => setShowSuggestions(!showSuggestions)}
              >
                Suggested follow-ups ({followUpQuestions.length})
                {showSuggestions ? <ChevronDownIcon /> : <ChevronUpIcon />}
              </button>
              {showSuggestions && <Box className="chat-suggestions">
                {React.Children.toArray(followUpQuestions.map((ques) =>
                  <button
                    type="button"
                    className="chat-suggestion"
                    disabled={chatLoading}
                    onClick={() => askFollowUPQuestion(ques)}
                  >
                    {ques}
                  </button>
                ))}
              </Box>}
            </Box> : chat.length !== 0 && chat[0].response !== "" ? <Box className="chat-footer">
              <InputGroup size='md'>
                <Input
                  className="chat-text-input"
                  pr='4.75rem'
                  type='text'
                  placeholder='Ask a follow-up question'
                  aria-label="Ask a follow-up question"
                  value={followUpQues}
                  onChange={(e) => setFollowUpQues(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendFollowUp() }}
                />
                <InputRightElement width='4.75rem'>
                  <Button className="chat-send" size='sm' onClick={sendFollowUp} isDisabled={!followUpQues.trim() || chatLoading}>
                    Send
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box> : null
      }
    </Box>
  )

  const ninjaCard = (
    <Box className="ninja-card">
      <Image className="ninja-card__avatar" src="/images/ninja.png" alt="" />
      <Box className="ninja-card__bubble">
        <Text className="ninja-card__title">
          {isLocked ? "Stuck on this one?" : chat.length > 0 ? "Pick up where you left off" : "Want to understand it?"}
        </Text>
        <Text className="ninja-card__text">
          {isLocked
            ? "Try the question first. Once you submit, I can walk you through it."
            : chat.length > 0
              ? "Your conversation about this question is still here."
              : "I can explain the solution step by step and answer your follow-up questions."}
        </Text>
      </Box>
      <Button className="ninja-card__action" onClick={openChat} isDisabled={isLocked}>
        <span aria-hidden="true">✨</span>
        {chat.length > 0 && !isLocked ? "Continue chat" : "Explain it step by step"}
      </Button>
      <Text className="ninja-card__disclaimer">AI Ninja can make mistakes. Please verify the solution.</Text>
    </Box>
  )

  return (
    <>
      {isFullScreen && (
        <Portal>
          <Box className="chat-backdrop" onClick={() => setIsExpanded(false)} />
          <Box className="chat-fullscreen" role="dialog" aria-modal="true" aria-label="Ask AI Ninja">
            {chatPanel}
          </Box>
        </Portal>
      )}
      {isDocked ? (
        <Box
          as="aside"
          className="ninja-dock"
          aria-label="Ask AI Ninja"
          sx={{
            top: `${dockOffset}px`,
            height: showChat && !isFullScreen ? `calc(100vh - ${dockOffset + 16}px)` : "auto",
          }}
        >
          {showChat ? (isFullScreen ? null : chatPanel) : ninjaCard}
        </Box>
      ) : (
        <>
          {!showChat && !isLocked && (
            <Button className="ninja-fab" onClick={openChat} aria-label="Ask AI Ninja">
              <Image src="/images/ninja.png" alt="" className="ninja-fab__avatar" />
              Ask AI Ninja
            </Button>
          )}
          {showChat && !isFullScreen && <Box className='chat-wrapper'>{chatPanel}</Box>}
        </>
      )}
      <CustomModal openModal={openModal} closeModal={closeModal} heading={"Submit feedback to NinjaLearner"} submitFeedback={submitFeedback} feedbackResponse={feedbackResponse}>
        <FormControl>
          <FormLabel fontSize='15px' fontFamily='Open Sans'>What went wrong?</FormLabel>
          <Textarea as="textarea" ref={initialRef} placeholder='Give as much detail as you can.' value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        </FormControl>
      </CustomModal>
    </>
  );
}
